import { useEffect, useRef, useState } from "react";
import Button from "~/components/common/Button";
import Toast from "~/components/common/Toast";
import {
  cancelProjectPromotion,
  createProjectPromotion,
  getProjectPromotion,
} from "~/services/projects";
import { formatPromotionDuration } from "~/utils/project";
import type {
  Project,
  ProjectPromotionState,
} from "~/types/project";
import styles from "./PromotionModal.module.css";

const formatNum = (n?: number) =>
  n == null ? "—" : n.toLocaleString("zh-CN");

// 「9 月 22 日」
const formatDate = (date?: string | null) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
};

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </svg>
);

type PromotionModalProps = {
  open: boolean;
  token: string;
  /** 可参与推广的项目（已发布到广场的） */
  projects: Project[];
  /** 初始选中的项目（从个人中心某个项目的入口进来时） */
  initialProjectId?: string;
  onClose: () => void;
  /** 下单 / 取消成功后通知父级刷新列表 */
  onChanged?: () => void;
  /** 去充值：先关掉本弹窗再唤起充值（TopUpModal 的层级低于本弹窗，不能叠着开） */
  onTopUp: () => void;
};

// 置顶推广弹窗（对齐设计稿 .bst）：五种状态 可购买 / 满位排队 / 审核中 / 已在排队 / 推广中
const PromotionModal = ({
  open,
  token,
  projects,
  initialProjectId,
  onClose,
  onChanged,
  onTopUp,
}: PromotionModalProps) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(
    initialProjectId || projects[0]?.id
  );
  const [data, setData] = useState<ProjectPromotionState | null>(null);
  const [loading, setLoading] = useState(false);
  const [planDays, setPlanDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // 下单被拒（电量不足）时的行内提示
  const [insufficient, setInsufficient] = useState<{
    balance: number;
    cost: number;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = (text: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = window.setTimeout(() => setToast(null), 2000);
  };

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    []
  );

  // 打开或切换项目时拉取推广状态
  useEffect(() => {
    if (!open || !selectedId || !token) return;
    let stale = false;
    setLoading(true);
    setConfirmingCancel(false);
    setInsufficient(null);
    getProjectPromotion(token, selectedId)
      .then((res) => {
        if (!stale) setData(res);
      })
      .catch((err: any) => {
        if (!stale) {
          setData(null);
          showToast(err?.message || "推广状态加载失败，请稍后重试");
        }
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [open, selectedId, token]);

  // 弹窗挂载时项目列表可能还没加载完（广场页打开时 mineProjects 尚为 null），
  // 打开期间保持一个有效的选中项；列表刷新后已选中的项目不变
  useEffect(() => {
    if (!open) return;
    setSelectedId((prev) => {
      if (prev && projects.some((p) => p.id === prev)) return prev;
      if (initialProjectId && projects.some((p) => p.id === initialProjectId))
        return initialProjectId;
      return projects[0]?.id;
    });
  }, [open, projects, initialProjectId]);

  if (!open) return null;

  const promotion = data?.promotion || null;
  const state = data?.state;
  const selectedProject = projects.find((p) => p.id === selectedId);
  // 推广中时顶部数据行换成推广期增量，其余状态显示近 7 天自然数据
  const dataRow =
    state === "active" && promotion
      ? {
          label: "推广期新增",
          impressions: `+${formatNum(promotion.impressions_delta)}`,
          upvotes: `+${formatNum(promotion.upvotes_delta)}`,
        }
      : {
          label: "近 7 天",
          impressions: formatNum(data?.stats?.impressions),
          upvotes: `+${formatNum(data?.stats?.upvotes)}`,
        };

  const handleSubmit = async () => {
    if (!selectedId || submitting) return;
    const plan = data?.plans?.find((p) => p.duration_days === planDays);
    setSubmitting(true);
    try {
      const res = await createProjectPromotion(token, selectedId, planDays);
      setData(res);
      setConfirmingCancel(false);
      setInsufficient(null);
      onChanged?.();
      showToast(
        res?.state === "pending_review"
          ? "申请已提交，审核通过后开始推广"
          : res?.state === "queued"
            ? "已进入排队，可在弹窗内取消"
            : "申请已提交"
      );
    } catch (err: any) {
      if (err?.error === "ele_too_low") {
        // 电量不足：行内提示，去充值走 onTopUp（先关本弹窗）
        setInsufficient({
          balance: data?.ele_balance ?? 0,
          cost: plan?.ele ?? 0,
        });
      } else {
        showToast(err?.message || "操作失败，请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    try {
      const res = await cancelProjectPromotion(token, selectedId);
      setData(res);
      setConfirmingCancel(false);
      onChanged?.();
      const canceledEle = formatNum(res?.promotion?.ele ?? promotion?.ele);
      showToast(
        state === "pending_review"
          ? `已取消申请，${canceledEle} 电量已退回`
          : `已取消排队，${canceledEle} 电量已退回`
      );
    } catch (err: any) {
      setConfirmingCancel(false);
      showToast(err?.message || "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStateBanner = () => {
    if (!data) return null;
    // 满位：还没有自己的订单，提示排队规则（后端无法预估开始时间）
    if (state === "full") {
      const occupied = data.slots?.occupied ?? 0;
      return (
        <div className={`${styles.state} ${styles.wait}`}>
          <ClockIcon />
          <div>
            <b>{occupied} 个位置都在推广中</b>
            ，现在购买会先进入审核，通过后按下单顺序排队展示。电量下单时扣除，审核或排队期间可以取消并原路退回。
          </div>
        </div>
      );
    }
    if (!promotion) return null;
    // 审核中：还没有排队/推广，不展示排队人数与预计时间
    if (state === "pending_review") {
      return (
        <div className={`${styles.state} ${styles.queue}`}>
          <ClockIcon />
          <div>
            <b>审核中</b> · 审核通过后开始计算推广时长。电量下单时已扣除，审核期间可以取消并原路退回。
          </div>
        </div>
      );
    }
    const ahead = promotion.queued_ahead_count;
    const estimate = formatDate(promotion.estimated_start_at);
    if (state === "queued") {
      return (
        <div className={`${styles.state} ${styles.queue}`}>
          <ClockIcon />
          <div>
            <b>排队中{ahead ? ` · 前面还有 ${ahead} 个项目` : " · 已排到下一位"}</b>
            {estimate && (
              <>
                ，预计 <b>{estimate}</b> 开始推广
              </>
            )}
            ，时长 {formatPromotionDuration(promotion.duration_days)}。电量下单时已扣除，排队期间可以取消并原路退回。
          </div>
        </div>
      );
    }
    if (state === "active") {
      return (
        <div className={`${styles.state} ${styles.live}`}>
          <CheckCircleIcon />
          <div>
            <b>推广中 · 剩余 {promotion.remaining_days ?? 0} 天</b>
            ，{formatDate(promotion.ended_at)} 到期。到期后可以再次购买。
          </div>
        </div>
      );
    }
    return null;
  };

  const renderAction = () => {
    if (!data) return null;
    if (state === "active") {
      return (
        <Button className={`${styles.action} ${styles.actionOff}`} ghost disabled>
          到期后可再次购买
        </Button>
      );
    }
    if ((state === "queued" || state === "pending_review") && promotion) {
      const cancelEle = formatNum(promotion.ele);
      if (confirmingCancel) {
        return (
          <div className={styles.confirmRow}>
            <span>
              {state === "pending_review"
                ? `取消申请后，${cancelEle} 电量原路退回。`
                : `取消后排队位置让出，${cancelEle} 电量原路退回。`}
            </span>
            <span className={styles.confirmBtns}>
              <Button
                size="sm"
                ghost
                disabled={submitting}
                onClick={() => setConfirmingCancel(false)}
              >
                再想想
              </Button>
              <Button
                size="sm"
                className={styles.dangerBtn}
                disabled={submitting}
                onClick={handleCancel}
              >
                确认取消
              </Button>
            </span>
          </div>
        );
      }
      return (
        <Button
          className={styles.action}
          ghost
          disabled={submitting}
          onClick={() => setConfirmingCancel(true)}
        >
          {state === "pending_review"
            ? `取消申请，退回 ${cancelEle} 电量`
            : `取消排队，退回 ${cancelEle} 电量`}
        </Button>
      );
    }
    // available 直接申请；full 购买进排队
    return (
      <Button className={styles.action} disabled={submitting || loading} onClick={handleSubmit}>
        {state === "full" ? "支付并排队" : "申请置顶"}
      </Button>
    );
  };

  return (
    <div className={styles.mask} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <span className={styles.title}>置顶推广</span>
          <a className={styles.close} onClick={onClose}>
            &times;
          </a>
        </div>

        {loading && !data ? (
          <div className={styles.loading}>加载中…</div>
        ) : (
          <>
            <div className={styles.pick}>
              <span className={styles.pickLabel}>选择项目</span>
              <span className={styles.sel}>
                <select
                  value={selectedId}
                  title={selectedProject?.name || ""}
                  onChange={(e) => {
                    setSelectedId(e.target.value);
                    setPlanDays(7);
                  }}
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className={styles.data}>
              {dataRow.label}：曝光 <b>{dataRow.impressions}</b> · 顶{" "}
              <b>{dataRow.upvotes}</b>
            </div>

            {renderStateBanner()}

            {state !== "active" && state !== "queued" && state !== "pending_review" && (
              <div className={styles.desc}>
                您可以通过电量来获得置顶推广位，使项目得到更多的曝光和支持
              </div>
            )}

            <div className={styles.plans}>
              {(data?.plans || []).map((plan) => (
                <button
                  key={plan.duration_days}
                  type="button"
                  className={`${styles.plan} ${
                    planDays === plan.duration_days ? styles.planOn : ""
                  }`}
                  onClick={() => {
                    setPlanDays(plan.duration_days);
                    setInsufficient(null);
                  }}
                >
                  <b>{plan.ele}</b>
                  <span>电量 / {formatPromotionDuration(plan.duration_days)}</span>
                </button>
              ))}
            </div>

            <div className={styles.balance}>
              剩余电量<b>{formatNum(data?.ele_balance)}</b>
              <a onClick={() => onTopUp()}>去充值 ›</a>
            </div>

            {insufficient && (
              <div className={`${styles.state} ${styles.wait}`}>
                <ClockIcon />
                <div>
                  <b>电量余额不足</b>
                  ：剩余 {formatNum(insufficient.balance)}，本次需要{" "}
                  {formatNum(insufficient.cost)}。充值后再来申请。
                </div>
              </div>
            )}

            <div className={styles.notes}>
              <div className={styles.note}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="7" rx="1.5" />
                  <rect x="3" y="14" width="18" height="6" rx="1.5" />
                </svg>
                置顶推广位目前仅 {data?.slots?.total ?? 2} 个，先付先得；后付顺延
              </div>
              <div className={styles.note}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3.5 12.5 11 5h8v8l-7.5 7.5a1.5 1.5 0 0 1-2.1 0l-5.9-5.9a1.5 1.5 0 0 1 0-2.1Z" />
                  <circle cx="15.5" cy="8.5" r="1.3" />
                </svg>
                置顶推广价格目前暂行，后续会按需逐渐调高
              </div>
              <div className={styles.note}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3 4.5 6v6c0 4.4 3.1 8.2 7.5 9 4.4-.8 7.5-4.6 7.5-9V6L12 3Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                请确保项目信息填写完整，需官方审核后显示
              </div>
            </div>

            {renderAction()}

            <div className={styles.foot}>
              推广不改变项目排行榜的名次，到期自动结束
            </div>
          </>
        )}
      </div>
      <Toast text={toast} />
    </div>
  );
};

export default PromotionModal;
